import NoImage from "@/assets/images/no-image.webp";
import { clsx } from "clsx";
import {
  format,
  isBefore,
  isWithinInterval,
  parse,
  parseISO,
  startOfToday,
} from "date-fns";
import he from "he";
import { twMerge } from "tailwind-merge";
import { brandList } from "./brandData";

import {
  OnOrderBadge,
  SpecialOrderBadge,
  StockBadge,
} from "@/components/Badges/Badges";
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const isValid = (name, errors) => {
  if (!name || !errors) return;
  return errors[name] ? true : false;
};

export const strengthBgColor = {
  "Too weak": "bg-red-800",
  Weak: "bg-[#FF0000]",
  Medium: "bg-[#FEC230]",
  Strong: "bg-[#039C03]",
};

export const strengthTextColor = {
  "Too weak": "text-red-800",
  Weak: "text-[#FF0000]",
  Medium: "text-[#FEC230]",
  Strong: "text-[#039C03]",
};

export const formatMonthDate = (dateString, updatedFormat = "MMMM dd, yyyy") =>
  dateString ? format(new Date(dateString), updatedFormat) : "";

export const formatDate = (dateString, updatedFormet = "dd MMM, h:mm a") =>
  dateString && format(new Date(dateString), updatedFormet);

export const formatISODate = (dateString, updatedFormet = "dd MMM, h:mm a") =>
  dateString && format(parseISO(dateString), updatedFormet);

export const formatAmount = (amount) =>
  (amount &&
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)) ||
  "$0.00";

export function capitalizeFirstLetter(string) {
  if (!string) return string; // Handle empty or undefined strings
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function formatCamelCase(string) {
  if (!string) return string;
  const spacedString = string.replace(/([a-z])([A-Z])/g, "$1 $2");
  return spacedString.split(" ").map(capitalizeFirstLetter).join(" ");
}

export function isRebateExpired(endDate) {
  // Parse the API-provided date string into a Date object
  const productEndDate = parseISO(endDate);

  // Get today's date at midnight
  const today = startOfToday();

  // Check if the productEndDate is before today
  return isBefore(productEndDate, today);
}

// Memoization cache for frequently decoded strings
const decodeCache = new Map();
export function decodeHTML(encodedStr, isHtmlOnly = false) {
  if (!encodedStr || typeof encodedStr !== "string") return "";

  const cacheKey = `${encodedStr}:${isHtmlOnly}`;
  if (decodeCache.has(cacheKey)) {
    return decodeCache.get(cacheKey) || "";
  }

  let cleanedStr = encodedStr
    .replace(/Â/g, "")
    .replace(/â¢/g, "•")
    .replace(/â/g, "–")
    .replace(/â”/g, "—")
    .replace(/â/g, "“")
    .replace(/â/g, "”")
    .replace(/â/g, "’")
    .replace(/â¢/g, "™")
    .replace(/â/g, "℠")
    .replace(/â©/g, "©")
    .replace(/â®/g, "®");

  const htmlDecoded = he.decode(cleanedStr);

  if (isHtmlOnly) {
    decodeCache.set(cacheKey, htmlDecoded);
    return htmlDecoded;
  }

  try {
    const fullyDecoded = decodeURIComponent(htmlDecoded);
    decodeCache.set(cacheKey, fullyDecoded);
    return fullyDecoded;
  } catch {
    decodeCache.set(cacheKey, htmlDecoded);
    return htmlDecoded;
  }
}

export function isWarehouseOpen() {
  const now = new Date();
  const day = now.getDay(); // 0 (Sunday) to 6 (Saturday)

  // Warehouse operating hours
  const warehouseTimings = {
    weekdayStartTime: "09:00:00",
    weekdayEndTime: "13:00:00",
    saturdayStartTime: "10:00:00",
    saturdayEndTime: "12:00:00",
  };

  // If it's Sunday, the warehouse is closed
  if (day === 0) return false;

  // Get the correct start and end times
  const startTimeStr =
    day === 6
      ? warehouseTimings.saturdayStartTime
      : warehouseTimings.weekdayStartTime;
  const endTimeStr =
    day === 6
      ? warehouseTimings.saturdayEndTime
      : warehouseTimings.weekdayEndTime;

  // Parse times
  const startTime = parse(startTimeStr, "HH:mm:ss", now);
  const endTime = parse(endTimeStr, "HH:mm:ss", now);

  // Check if the current time is within the warehouse open interval
  return isWithinInterval(now, { start: startTime, end: endTime });
}

export function getDisabledDays(daysConfig) {
  if (!daysConfig || daysConfig?.length <= 0) return {};
  // Map day names to their corresponding indices
  const dayNameToIndex = {
    SUNDAY: 0,
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
  };

  // Create an array of inactive day indices
  const inactiveDays = daysConfig
    .filter((day) => !day.isActive)
    .map((day) => dayNameToIndex[day.name]);

  // Return the disabled prop value
  return inactiveDays.length > 0 ? { dayOfWeek: inactiveDays } : {};
}

export function getDisabledDates(dateStrings) {
  if (!dateStrings || dateStrings.length <= 0) return [];
  return dateStrings.map((dateString) => parseISO(dateString));
}

export const roundPercentage = (percentage) => {
  if (!percentage || isNaN(percentage)) return "";
  const rounded = Math.round(percentage);
  return rounded ? `(${rounded}%)` : "";
};

export async function openChatBotFunction() {
  const floatingBubble = document.getElementsByClassName("dimelo-chat-round-button")[0];
  if (floatingBubble && floatingBubble instanceof HTMLElement) {
    console.log("💬 Chat already initialized — toggling chat bubble");
    floatingBubble.click();
    return;
  }
  const initialLauncher = document.querySelector('[id^="dimelo_chat_button_"]');
  if (initialLauncher && initialLauncher instanceof HTMLElement) {
    console.log("📨 Initial launcher found — triggering first-time open");
    initialLauncher.click();
  } else {
    console.warn("❌ Neither chat bubble nor initial launcher found.");
  }
};

// export const openChatBotFunction = () => {
//   const chatFrame = document.getElementById("embeddedMessagingFrame");
//   const chatButton = document.getElementById(
//     "embeddedMessagingConversationButton"
//   );
//   if (chatFrame && chatFrame.classList.contains("isMinimized")) {
//     chatFrame.classList.remove("isMinimized");
//     chatFrame.classList.add("isMaximized");
//   } else if (chatFrame && chatFrame.classList.contains("isMaximized")) {
//     chatFrame.classList.remove("isMaximized");
//     chatFrame.classList.add("isMinimized");
//   } else {
//   chatButton.click();
//   }
// };

export const getBrandImage = (brandName) => {
  const normalizedBrandName = (typeof brandName === "string" ? brandName : "")
    .toLowerCase()
    .trim();
  if (!normalizedBrandName) {
    return NoImage.src;
  }
  const brand = brandList?.find((b) => {
    const bName = (b.name || "").toLowerCase();
    const matches =
      bName.includes(normalizedBrandName) ||
      normalizedBrandName.includes(bName);
    return matches;
  });
  // Return image or fallback
  return brand?.src?.src || NoImage.src;
};

export const renderStockBadge = (isInStock, orderStatus, className) => {
  if (!orderStatus) {
    return <StockBadge isInStock={isInStock} className={className} />;
  }

  if (orderStatus === "On Order") {
    return <OnOrderBadge className={className} />;
  }

  return <SpecialOrderBadge className={className} />;
};
